/**
 * @module Home
 * @description Home Page
 */
import React, { FC, useContext, useEffect } from "react";
import Header from "~/core/Header";
import Footer from "~/core/Footer";
import Template from "ui/templates/Default";
import BannerServiceCheckSingleLine from "~/blocks/Banners/BannerServiceCheckSingleLine";
import HeroProductCardsRedesign from "~/blocks/Heroes/HeroProductCardsRedesign";
import BannerDropdownList from "~/blocks/Banners/BannerDropdownList";
import ProductTabset from "~/blocks/ProductTabset";
import ColumnContentCarousel from "~/blocks/ColumnContent/ColumnContentCarousel";
import TrackElementViewed from "~/components/helpers/TrackElementViewed";
import { CustomCohesionContext } from "ui/providers/cohesion";
import { useRouter } from "next/router";
import { MonarchSlot } from '@redventures/cohesion-utils-react';
import { withPricing } from "ui/providers/pricing";
import { Pricing } from 'ui/types/common';

import COLUMN_CONTENT_CAROUSEL_DEFAULT_PROPS from "~/constants/ColumnContentCarouselDefaults";
import PRODUCT_CARDS_HERO_DEFAULT_PROPS from "~/constants/ProductCardRedesignDefaults";
import PRODUCT_TABSET_DEFAULT_PROPS from "~/constants/ProductTabsetDefaults";

type Props = {
  bakedPricing: Pricing
}

export const getStaticProps = (async () => {
  const res = await fetch('https://cdn.cohesionapps.com/monarch/api/v1/evaluate/rule/203e9505-3a67-4b30-9b0b-6cf049a4591c/lpPricing', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Token': '7f339099-a47f-496c-96aa-b6f5b79bb8b3'
    },
  });
  const data = await res.json();
  return { props: { bakedPricing: data?.lpPricing } }
});

const PageBase = () => {
  const router = useRouter();
  const { monarchReady } = useContext(CustomCohesionContext);

  //   // When the user clicks on the Offers menu, they need to be redirected to the section where the offers are displayed.
  //   // This content comes from Preamp, and for now, we will use tabItem-0 as a reference ID.
  //   // The setInterval is in place because the content isn’t always available immediately,
  //   // so after two attempts, it will stop

  useEffect(() => {
    const querySection = router.query.section;

    if (querySection && querySection === "tabItem-0" && monarchReady) {
      let attempts = 0;

      const checkElement = setInterval(() => {
        const element = document.getElementById(querySection);
        const header = document.querySelector("#masthead");
        const sectionHeader = document.querySelector("section[data-sticky]");

        if (element && header && sectionHeader) {
          const offset = header?.clientHeight + sectionHeader.clientHeight;

          const scrollPosition =
            element.getBoundingClientRect().top + window.scrollY - offset;

          window.scrollTo({
            top: scrollPosition,
            behavior: "smooth",
          });
          clearInterval(checkElement);
        } else if (++attempts >= 2) {
          clearInterval(checkElement);
        }
      }, 1000);

      return () => clearInterval(checkElement);
    }
  }, [router.query.section, monarchReady]);

  return (
    <>
      <Template
        title="Home"
        header={<Header />}
        footer={<Footer />}
        hero={
          <>
            {/* Page One | Above Hero */}
            <MonarchSlot slotId="smbLpPageOneAboveHero">
              <BannerServiceCheckSingleLine isSticky={ true } />
            </MonarchSlot>

            {/* Page One | Hero */}
            <MonarchSlot slotId="smbLpPageOneHero">
              <HeroProductCardsRedesign
                position="BASE SITE"
                {...PRODUCT_CARDS_HERO_DEFAULT_PROPS}
              />
            </MonarchSlot>
          </>
        }
      >
        <>
          {/* Page One | Section 1 */}
          <TrackElementViewed position="SECTION 1 - PLAN FEATURES BANNER">
            <MonarchSlot slotId="smbLpPageOneSection1">
              <BannerDropdownList />
            </MonarchSlot>
          </TrackElementViewed>

          {/* Page One | Section 2 */}
          <MonarchSlot slotId="smbLpPageOneSection2">
            {/* Empty by default since removing NFLST section  */}
          </MonarchSlot>

          {/* Page One | Section 3 */}
          <MonarchSlot slotId="smbLpPageOneSection3">
            <ProductTabset
              position="SECTION 3"
              {...PRODUCT_TABSET_DEFAULT_PROPS}
            />
          </MonarchSlot>

          {/* Page One | Section 4 */}
          <TrackElementViewed position="SECTION 4">
            <MonarchSlot slotId="smbLpPageOneSection4"/>
          </TrackElementViewed>

          {/* Page One | Section 5 */}
          <MonarchSlot slotId="smbLpPageOneSection5">
            <ColumnContentCarousel
              position="SECTION 5"
              {...COLUMN_CONTENT_CAROUSEL_DEFAULT_PROPS}
            />
          </MonarchSlot>

          {/* Page One | Sticky CTA */}
          <MonarchSlot slotId="smbLpPageOneStickyCta"/>
        </>
      </Template>
    </>
  );
};

const Page = ({ bakedPricing }: Props) => {
  const WrappedPage = withPricing(PageBase, bakedPricing);
  return <WrappedPage />;
}

export default Page;
